import { useNavigate } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IAsset } from '~/types/assets-management'
import type { IPageContainer, IPageContainerCreateUpdate } from '~/types/page-container'
import type { ITemplate } from '~/types/template'

import PageContainerForm from '../Form'
import TemplateNotFound from '../TemplateNotFound'

import MwMLoader from '~/components/MwMLoader'
import { useTemplateConfigWithTranslationValues } from '~/hooks/useTemplateConfig'
import { useAssetDetailsQueries } from '~/queries/assets'
import { useIapListBySkuQuery } from '~/queries/iap'
import { useCreatePageContainerFromTemplateMutation } from '~/queries/page-containers'
import { useTranslationValuesQuery } from '~/queries/translation'
import { PAGE_CONTAINER_STATUS } from '~/types/page-container'
import { formatPageContainerData } from '~/utils/page-container'
import { displayToast } from '~/utils/toast'

const PageContainerDuplication = ({
  pageContainer,
  selectedApp,
  template,
}: {
  pageContainer: IPageContainer
  selectedApp: IAppSummary
  template: ITemplate
}): ReactElement => {
  const navigate = useNavigate()

  const { template_key: templateKey, template_version: templateVersion } = pageContainer

  const { isLoading: isTemplateLoading, templateConfig } = useTemplateConfigWithTranslationValues({
    applicationSummary: selectedApp,
    templateKey,
  })

  const skus = pageContainer.placeholder_iap_values?.map(({ id }) => id).filter((id) => !!id)

  const { data: iaps, isLoading: isIapPurchaseLoading } = useIapListBySkuQuery(
    {
      appKey: selectedApp.app_key,
      deviceType: selectedApp.device_type,
      skus: skus as string[],
    },
    { enabled: !!selectedApp.app_key && !!selectedApp.device_type && !!skus?.length }
  )

  const assetIds = pageContainer?.placeholder_asset_values?.map(({ id }) => id || '')

  const assetQueries = useAssetDetailsQueries(assetIds)

  const assets = assetQueries.some((e) => e.isLoading) ? [] : assetQueries.map(({ data }) => data as IAsset)

  const translationKeys = pageContainer?.placeholder_text_values?.map(({ translation_key }) => translation_key) || []

  const { data: translationsData } = useTranslationValuesQuery({ application: selectedApp, translationKeys })

  const pageContainerDetails = formatPageContainerData({
    assets,
    iaps,
    pageContainer: { ...pageContainer, status: PAGE_CONTAINER_STATUS.DRAFT },
    translations: translationsData || [],
  })

  const { mutateAsync: createPageContainerMutation } = useCreatePageContainerFromTemplateMutation()

  const handleSubmit = async (data: IPageContainerCreateUpdate) => {
    try {
      const pageContainer = await createPageContainerMutation(data)
      displayToast('Page container successfully saved', 'success')
      navigate(`/dynamic-screens/page-containers/${pageContainer.uuid}`)
    } catch {
      displayToast('Page container could not be duplicated', 'error')
    }
  }

  const isLoading = isTemplateLoading || (isIapPurchaseLoading && skus?.length) || assetQueries.some((e) => e.isLoading)

  if (isLoading) {
    return (
      <div className="no-template-container">
        <MwMLoader category="item" />
      </div>
    )
  }

  return templateConfig && templateVersion && selectedApp ? (
    <PageContainerForm
      appSummary={selectedApp}
      canEditVersion
      isUnity={pageContainerDetails.is_unity}
      onSubmit={handleSubmit}
      pageContainerDetails={pageContainerDetails}
      resetHeaderDetails
      template={template}
      templateConfig={templateConfig}
      templateVersion={templateVersion}
    />
  ) : (
    <TemplateNotFound pageContainer={pageContainer} selectedApp={selectedApp} templateKey={templateKey} />
  )
}

export default PageContainerDuplication
